import React, {
  Dispatch,
  FC,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect, useMemo,
  useRef,
  useState,
} from "react";
import {
  AddBeneficiary,
  ButtonBox,
  InputDatePicker,
  ModelDialogBox,
  SelectBox,
  TextBox,
  UploadDialog,
} from "../..";
import {
  getLabelName,
  getLookUpItemValue,
  getOriginalValueInCurrency, handleSelectInputChange,
  isArabicCurrentLanguage,
  setFormikFieldValueByName,
  updateStateDynamically,
} from "../../../utils";
import {
  CustomerComponentDisplayModeEnum,
  CustomerResponseModel,
  InputDatePickerTimeDisplayModeEnum,
  KeyValueStateModel,
  LookupItemModel,
  PageEnum,
  PaymentTypeEnum,
  RequestActionModel,
  SupplierCustomerTypeEnum,
  TextBoxVersionEnum,
  TransactionClientConfigurationModel,
  TransactionDetailResponseModel,
  TransactionItemResponseModel,
  TransactionTotalValuesModel,
  UserConfigurationModel,
} from "../../../models";
import {
  customerInitialValue,
  getAccountBalance,
  getCurrencyValueEntity,
  getItemTaxPercentageValue,
  getTextBoxValue,
  handleCalculateNewItemDiscountValuesChange,
  handleCalculateNewItemValuesChange,
  handleChangeTransactionDiscount,
  handleChangeTransactionDiscountRatio, handleSearchAndFilterBeneficiary,
  handleTransactionTotalValuesByTransactionItems,
  updateTransactionItemsCurrencyValueAndReCalculateAllValues,
} from "../common/businessLogic/transactionBl";
import {
  getItemSalesPrice,
  isConfiguredForSavePurchaseFromTransaction,
} from "../common/businessLogic/commonBl";
import { debounce } from "lodash";
import { UploadedFileModel } from "../../../models";
import {InputActionMeta} from "react-select";
const RegisterTransactionHeaderComponent: FC<{
  formik: any;
  data: TransactionDetailResponseModel;
  transactionItemObject: TransactionItemResponseModel;
  userConfiguration: UserConfigurationModel;
  transactionConfiguration: TransactionClientConfigurationModel;
  lookups: Record<string, LookupItemModel[]>;
  lookupRefs: Record<string, React.MutableRefObject<any>>;
  states: Record<string, Dispatch<SetStateAction<any>>>;
  shouldReset: boolean;
  setUploadedFiles: React.Dispatch<React.SetStateAction<UploadedFileModel[]>>;
  uploadedFiles: UploadedFileModel[];
  //onActionEvent: (o: RequestActionModel) => void;
}> = ({
  formik,
  data,
  transactionItemObject,
  userConfiguration,
  transactionConfiguration,
  lookups,
  lookupRefs,
  states,
  setUploadedFiles,
  uploadedFiles,
  shouldReset = false, //onActionEvent = () => {}
}) => {
  //#region variables
  const discountPercentageMaxValue: number = 50;
  const {
    beneficiaryList = [],
    calcTypeList = [],
    currencyList = [],
    paymentTypeList = [],
    storeEmployeeList = [],
    costCenterList = [],
    itemList = [],
    originalBeneficiaryList=[]
  } = lookups;
  const {
    calcTypeSelectBoxMultiselectRef,
    paymentTypeSelectBoxMultiselectRef,
    currencySelectBoxMultiselectRef,
    storeEmployeeSelectBoxMultiselectRef,
    costCenterSelectBoxMultiselectRef,
    paymentTypeForSupplierPaymentSelectBoxMultiselectRef,
  } = lookupRefs;
  const { setData, setTransactionItemObject, setBeneficiaryList } = states;
  // const configChecker = isConfiguredForSavePurchaseFromTransactionByTransactionType(userConfiguration);
  // const cansSavePurchaseTransactionFromSales = configChecker(transactionConfiguration.transactionType);
  const canSavePurchaseTransactionFromSales = useMemo(() => {
    return isConfiguredForSavePurchaseFromTransaction(
        transactionConfiguration.transactionType,
        userConfiguration
    );
  }, [transactionConfiguration.transactionType, userConfiguration]);
  //#endregion
  //#region state
  const [localData, setLocalData] =
    useState<TransactionDetailResponseModel>(data);
  const [localFormik, serLocalFormik] = useState(formik);
  const isDirty = useRef(false);
  const componentRef = useRef<HTMLDivElement>(null);
  const isFocusedInComponent = useRef(false);
  const prevResetSignal = useRef(shouldReset);
  const [showAddBeneficiaryModel, setShowAddBeneficiaryModel] = useState(false);
  const [customerObject] =
    useState<CustomerResponseModel>(customerInitialValue);
  //#endregion
  //#region functions
  const updateLocalData = useCallback((field: string, value: any | null) => {
    setLocalData((prev) => ({
      ...prev,
      [field]: value,
    }));
    //@ts-ignore
    serLocalFormik((prev) => ({
      ...prev,
      [field]: value,
    }));
    isDirty.current = true;
  }, []);
  const syncToParent = useCallback(() => {
    if (isDirty.current) {
      //onActionEvent({request: {...data, Code: generateGuid()}, action: ActionTypeEnum.UpdateParentStatusFromLocalStatus});
      setData((prevData: TransactionDetailResponseModel) => ({
        ...prevData,
        Code: localData.Code,
        Note: localData.Note,
      }));
      formik.setFieldValue("Code", localData.Code);
      formik.setFieldValue("Note", localData.Note);
      isDirty.current = false;
    }
  }, [localData, localFormik, setData, formik, data]);
  const handleFocusIn = useCallback(() => {
    isFocusedInComponent.current = true;
  }, []);
  const handleFocusOut = useCallback(
    (e: React.FocusEvent) => {
      // Check if focus is leaving the component entirely

      if (
        componentRef.current &&
        !componentRef.current.contains(e.relatedTarget as Node | null)
      ) {
        isFocusedInComponent.current = false;
        if (isDirty.current) {
          syncToParent();
        }
      }
    },
    [syncToParent]
  );
  const handleComponentBlur = useCallback(() => {
    syncToParent();
  }, [syncToParent]);
  const updateTransactionItemObjectState = async (
    key?: string | null,
    value?: any | null,
    keyValues?: KeyValueStateModel[] | null
  ) => {
    await updateStateDynamically(
      setTransactionItemObject,
      transactionItemObject,
      key,
      value,
      keyValues
    );
  };
  const debouncedUpdateState = debounce(
    async (setState, stateValue, keyValues) => {
      await updateStateDynamically(setState, stateValue, null, null, keyValues);
    },
    300
  );
  const handleCodeChange = useCallback(
    //@ts-ignore
    (e) => {
      const value = getTextBoxValue(e);
      setFormikFieldValueByName(formik, "Code", value);
      //@ts-ignore
      setData((prevData) => ({
        ...prevData,
        Code: value,
      }));
    },
    [formik, setData]
  );
  //#endregion
  //#region useEffect
  useEffect(() => {
    if (shouldReset && !prevResetSignal.current) {
      setLocalData({
        ...data,
        Code: data.Code || "",
        Note: data.Note || "",
      });
      isDirty.current = false;
    }
    prevResetSignal.current = shouldReset;
  }, [data, shouldReset]);
  //#endregion
  //#region html
  return (
    <div
      //onBlur={handleComponentBlur}
      ref={componentRef}
      onFocus={handleFocusIn}
      onBlur={handleFocusOut}
    >
      <div
          className="row row-cols-1 row-cols-xxl-3 row-cols-xl-3 row-cols-lg-3 row-cols-md-2 row-cols-sm-1 g-md-4 g-sm-4"
          // className="row row-cols-1 row-cols-sm-1 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 row-cols-xxl-2 g-md-4 g-sm-4"
      >
        <div className="col-xl-4 col-lg-4">
          <TextBox
            labelName={getLabelName("Code")}
            isReadOnly={
              !userConfiguration?.userSetting?.EnableManualInvoiceCode ?? true
            }
            inputName={"Code"}
            errorText={formik.errors.Code}
            // inputValue={formik.values.Code}
            // onChange={formik.handleChange}
            // onBlur={formik.handleBlur}

              type="text"
              inputValue={localData.Code}
              onChange={async (e: any) => {
                const value = getTextBoxValue(e);
                updateLocalData("Code", value);
                // setFormikFieldValueByName(formik,"Code",value);
                // await updateStateDynamically(setData, data, null, null, [
                //   { key: "Code", value: value},
                // ]);
              }}
              //onChange={handleCodeChange}
              onBlur={formik.handleBlur}
            />
          </div>
          <div
              className="col-xl-8 col-lg-8 flex-form-control"
          >
            <SelectBox
              id="customersSelectBox"
              labelName={
                transactionConfiguration.isSalesComponent
                  ? getLabelName("Customer")
                  : getLabelName("Supplier")
              }
              isSingleSelect={true}
              selectedValues={
                data.Customer_ID != null
                  ? [data.Customer_ID.toString()]
                  : []
              }
              source={beneficiaryList}
              //multiselectRef={customerSelectBoxMultiselectRef}
              //@ts-ignore
              multiselectRef={(node: MutableRefObject<any>) =>
                node ? (lookupRefs["customerSelectBoxMultiselectRef"].current = node) : null
              }
              onInputChange={(inputValue: string, actionMeta: InputActionMeta) => {
                return handleSelectInputChange(inputValue, actionMeta, {
                  localList: beneficiaryList||[],
                  apiSearchFn: async (searchValue) => {
                    await handleSearchAndFilterBeneficiary({
                      setState:setBeneficiaryList,
                      stateList:beneficiaryList,
                      originalStateList:originalBeneficiaryList,
                      beneficiaryType:transactionConfiguration.isSalesComponent ? SupplierCustomerTypeEnum.Customer: SupplierCustomerTypeEnum.Supplier,
                      searchValue: searchValue,
                    });
                  }
                });
              }}
              onStatusChange={async (e: LookupItemModel) => {
                const val = e ? getLookUpItemValue(e) : null;
                let balance: number = 0;
                if (val != null) {
                  balance = await getAccountBalance(
                    Number(val || 0),
                    data.Currency_ID !== null && data.Currency_ID !== undefined
                      ? data.Currency_ID
                      : null,
                    beneficiaryList
                  );
                }
                await updateStateDynamically(setData, data, null, null, [
                  { key: "Customer_ID", value: val != null ? val : null },
                  { key: "balance", value: balance },
                ]);
              }}
              errorText={formik.errors.Customer_ID}
              controlSize={"w-100 w-calc"}
            ></SelectBox>
            <ButtonBox
              variant=""
              className="btn-gradient-primary btn-field-group"
              onClick={() => {
                setShowAddBeneficiaryModel(true);
              }}
            >
              <span className="sr-only">Add</span>
              <i className="mdi mdi-plus"></i>
            </ButtonBox>
          </div>
          <ModelDialogBox
            isXCloseButtonVisible={true}
            isModelVisible={showAddBeneficiaryModel}
            isCloseButtonVisible={false}
            isEscapeCloseEnabled={true}
            title={
              transactionConfiguration.isSalesComponent
                ? getLabelName("Customers")
                : getLabelName("Supplier")
            }
            size="xl"
            onCloseEvent={() => {
              setShowAddBeneficiaryModel(false);
            }}
          >
            <AddBeneficiary
              type={
                transactionConfiguration.isSalesComponent
                  ? SupplierCustomerTypeEnum.Customer
                  : SupplierCustomerTypeEnum.Supplier
              }
              request={customerObject}
              displayMode={
                CustomerComponentDisplayModeEnum.defaultMode
              }
              onActionEvent={async function (
                o: RequestActionModel
              ): Promise<void> {
                if (o.request !== null && o.request !== undefined) {
                  setBeneficiaryList([
                    ...beneficiaryList,
                    {
                      value: o.request.ID,
                      name: o.request.Name_En,
                      nameAr: o.request.Name,
                      otherValue: {
                        accountId: o.request.Account_ID,
                      },
                      isAdded: true,
                    },
                  ]);

                await updateStateDynamically(setData, data, null, null, [
                  { key: "Customer_ID", value: o.request.ID },
                  { key: "balance", value: 0 },
                ]);

                  // Force refresh of customer SelectBox by clearing and resetting its value
                  if (lookupRefs.customerSelectBoxMultiselectRef?.current) {
                    lookupRefs.customerSelectBoxMultiselectRef.current.setValue({
                      value: o.request.ID.toString(),
                      label: isArabicCurrentLanguage() ? o.request.Name : o.request.Name_En
                    });
                  }
                  setShowAddBeneficiaryModel(false);
                }
              }}
              formId={transactionConfiguration.isSalesComponent ? PageEnum.Customers : PageEnum.Suppliers}
            />
          </ModelDialogBox>
        <div className="col-xl-4 col-lg-4">
            <TextBox
              labelName={getLabelName("Balance")}
              inputValue={data.balance}
              isReadOnly={true}
              inputName="balance"
            />
          </div>
          {userConfiguration.userTransactionSetting.ShowCalcType && calcTypeList.length > 1 && (
              <div className="col-xl-4 col-lg-4">
              <SelectBox
                id="calcTypesSelectBox"
                labelName={getLabelName("Calc Type")}
                isSingleSelect={true}
                selectedValues={
                  data.CalcType_ID !== null && data.CalcType_ID !== undefined
                    ? [data.CalcType_ID.toString()]
                    : []
                }
                source={calcTypeList}
                //multiselectRef={calcTypeSelectBoxMultiselectRef}
                //@ts-ignore
                multiselectRef={(node: MutableRefObject<any>) =>
                  node ? (calcTypeSelectBoxMultiselectRef.current = node) : null
                }
                onStatusChange={async (e: LookupItemModel) => {
                  const val = getLookUpItemValue(e);
                  if (val !== null && val !== undefined) {
                    if (
                      transactionItemObject.ItemInstore_ID != null &&
                      transactionItemObject.ItemInstore_ID > 0
                    ) {
                      const itemSalePrice: number =
                        transactionItemObject.ItemInstore_ID != null
                          ? getItemSalesPrice(
                              transactionItemObject.ItemUnit_ID || 0,
                              transactionConfiguration.transactionType,
                              val != null ? Number(val) : null,
                              itemList,
                              calcTypeList,
                              userConfiguration.userTransactionSetting
                            )
                          : 0;
                      const itemPriceCalculatedValues =
                        handleCalculateNewItemValuesChange(
                          itemList,
                          transactionItemObject.ItemUnit_ID || 0,
                          itemSalePrice,
                          data.ValueCurrency,
                          true,
                          false
                        );
                      const discountCalculatedValues =
                        handleCalculateNewItemDiscountValuesChange(
                          transactionItemObject.ItemDiscount,
                          data.ValueCurrency,
                          false
                        );
                      const itemTaxPercentage =
                        transactionItemObject.ItemInstore_ID !== null
                          ? getItemTaxPercentageValue(
                              transactionItemObject.ItemUnit_ID || 0,
                              itemList
                            )
                          : 0;
                      await updateTransactionItemObjectState(null, null, [
                        { key: "TaxPercentge", value: itemTaxPercentage },
                        {
                          key: "Unit_Price",
                          value: itemPriceCalculatedValues.UnitPrice,
                        }, //itemSalePrice
                        {
                          key: "UnitPriceWithTaxCalculated",
                          value: itemPriceCalculatedValues.UnitPriceWithTax,
                        },
                        {
                          key: "Unit_PriceInCurrencyCalculated",
                          value: itemPriceCalculatedValues.UnitPriceInCurrency,
                        },
                        {
                          key: "UnitPriceWithTaxInCurrency",
                          value:
                            itemPriceCalculatedValues.UnitPriceWithTaxInCurrency,
                        },
                        {
                          key: "ItemDiscount",
                          value: discountCalculatedValues.ItemDiscount,
                        },
                        {
                          key: "ItemDiscountInCurrency",
                          value:
                            discountCalculatedValues.ItemDiscountInCurrency,
                        },
                      ]);
                    }
                    await updateStateDynamically(
                      setData,
                      data,
                      "CalcType_ID",
                      val
                    );
                  } else {
                    await updateStateDynamically(setData, data, null, null, [
                      { key: "CalcType_ID", value: val },
                    ]);
                  }
                }}
                errorText={formik.errors.CalcType_ID}
              />
            </div>
          )}
          {userConfiguration.userTransactionSetting.ShowPaymentType && (
              <div className="col-xl-4 col-lg-4">
              <SelectBox
                id="paymentTypesSelectBox"
                labelName={getLabelName("Payment Type")}
                isSingleSelect={true}
                selectedValues={
                  data.PaymentType_ID !== null && data.PaymentType_ID !== undefined
                    ? [data.PaymentType_ID.toString()]
                    : []
                }
                source={paymentTypeList}
                //multiselectRef={paymentTypeSelectBoxMultiselectRef}
                //@ts-ignore
                multiselectRef={(node: MutableRefObject<any>) =>
                  node ? (paymentTypeSelectBoxMultiselectRef.current = node) : null
                }
                onStatusChange={async (e: LookupItemModel) => {
                  const val = getLookUpItemValue(e);
                  await updateStateDynamically(setData, data, null, null, [
                    { key: "PaymentType_ID", value: val },
                  ]);
                }}
                errorText={formik.errors.PaymentType_ID}
              />
            </div>
          )}
          {userConfiguration.userTransactionSetting.ShowCurrency && currencyList.length > 1 && (
              <div className="col-xl-4 col-lg-4">
              <SelectBox
                id="currencySelectBox"
                labelName={getLabelName("Currency")}
                isSingleSelect={true}
                selectedValues={
                  data.Currency_ID !== null && data.Currency_ID !== undefined
                    ? [data.Currency_ID.toString()]
                    : []
                }
                source={currencyList}
                //multiselectRef={currencySelectBoxMultiselectRef}
                //@ts-ignore
                multiselectRef={(node: MutableRefObject<any>) =>
                  node ? (currencySelectBoxMultiselectRef.current = node) : null
                }
                onStatusChange={async (e: LookupItemModel) => {
                  const val = getLookUpItemValue(e);
                  if (val !== null) {
                    const currencyValueEntity: number = getCurrencyValueEntity(
                      Number(val || 0),
                      currencyList
                    );
                    const balance: number = await getAccountBalance(
                      data.Customer_ID !== null &&
                        data.Customer_ID !== undefined
                        ? data.Customer_ID
                        : 0,
                      Number(val || 0),
                      beneficiaryList
                    );
                    const itemPriceCalculatedValues =
                      handleCalculateNewItemValuesChange(
                        itemList,
                        transactionItemObject.ItemInstore_ID || 0,
                        transactionItemObject.Unit_Price,
                        currencyValueEntity,
                        true,
                        false
                      );
                    const discountCalculatedValues =
                      handleCalculateNewItemDiscountValuesChange(
                        transactionItemObject.ItemDiscount,
                        currencyValueEntity,
                        false
                      );
                    await updateTransactionItemObjectState(null, null, [
                      {
                        key: "Unit_Price",
                        value: itemPriceCalculatedValues.UnitPrice,
                      }, //itemSalePrice
                      {
                        key: "UnitPriceWithTaxCalculated",
                        value: itemPriceCalculatedValues.UnitPriceWithTax,
                      },
                      {
                        key: "Unit_PriceInCurrencyCalculated",
                        value: itemPriceCalculatedValues.UnitPriceInCurrency,
                      },
                      {
                        key: "UnitPriceWithTaxInCurrency",
                        value:
                          itemPriceCalculatedValues.UnitPriceWithTaxInCurrency,
                      },
                      {
                        key: "ItemDiscount",
                        value: discountCalculatedValues.ItemDiscount,
                      },
                      {
                        key: "ItemDiscountInCurrency",
                        value: discountCalculatedValues.ItemDiscountInCurrency,
                      },
                      { key: "ValueOfCurrency", value: currencyValueEntity },
                    ]);

                    const transactionItems =
                      updateTransactionItemsCurrencyValueAndReCalculateAllValues(
                        data.TransactionDetaillist,
                        currencyValueEntity
                      );

                    const transactionValues: TransactionTotalValuesModel =
                      handleTransactionTotalValuesByTransactionItems(
                        transactionItems
                      );
                    await updateStateDynamically(setData, data, null, null, [
                      { key: "Currency_ID", value: val },
                      { key: "ValueCurrency", value: currencyValueEntity },
                      { key: "balance", value: balance },
                      { key: "TotalMony", value: transactionValues.totalMoney },
                      { key: "TotalNet", value: transactionValues.totalNet },
                      { key: "TotalTax", value: transactionValues.totalTax },
                      {
                        key: "Discount",
                        value: transactionValues.totalDiscount,
                      },
                      {
                        key: "TotalMonyInCurrency",
                        value: transactionValues.totalMoneyInCurrency,
                      },
                      {
                        key: "TotalNetInCurrency",
                        value: transactionValues.totalNetInCurrency,
                      },
                      {
                        key: "TotalTaxInCurrency",
                        value: transactionValues.totalTaxInCurrency,
                      },
                      {
                        key: "DiscountInCurrency",
                        value: transactionValues.totalDiscountInCurrency,
                      },
                      {
                        key: "discountRatio",
                        value: transactionValues.totalDiscountPercentage,
                      },
                      { key: "TransactionDetaillist", value: transactionItems },
                    ]);
                  } else {
                    await updateStateDynamically(setData, data, null, null, [
                      { key: "Currency_ID", value: val },
                    ]);
                  }
                }}
                errorText={formik.errors.Currency_ID}
              />
            </div>
          )}
          {userConfiguration.userTransactionSetting.ShowEmployeeResponsibile && storeEmployeeList.length > 1 && (
              <div className="col-xl-4 col-lg-4">
              <SelectBox
                id="storeEmployeesSelectBox"
                labelName={getLabelName("Employee")}
                isSingleSelect={true}
                selectedValues={
                  data.Emp_ID !== null && data.Emp_ID !== undefined
                    ? [data.Emp_ID.toString()]
                    : []
                }
                source={storeEmployeeList}
                //multiselectRef={storeEmployeeSelectBoxMultiselectRef}
                //@ts-ignore
                multiselectRef={(node: MutableRefObject<any>) =>
                  node
                    ? (storeEmployeeSelectBoxMultiselectRef.current = node)
                    : null
                }
                onStatusChange={async (e: LookupItemModel) => {
                  const val = getLookUpItemValue(e);
                  await updateStateDynamically(setData, data, null, null, [
                    { key: "Emp_ID", value: val },
                  ]);
                }}
                errorText={formik.errors.Emp_ID}
              />
            </div>
          )}
        {userConfiguration.userTransactionSetting.ShowCostCenter && (
            <div className="col-xl-4 col-lg-4">
            <SelectBox
              id="costCenterSelectBox"
              labelName={getLabelName("CostCenter")}
              isSingleSelect={true}
              selectedValues={
                data.CostCenter_ID !== null && data.CostCenter_ID !== undefined
                  ? [data.CostCenter_ID.toString()]
                  : []
              }
              source={costCenterList}
              //multiselectRef={costCenterSelectBoxMultiselectRef}
              //@ts-ignore
              multiselectRef={(node: MutableRefObject<any>) =>
                node ? (costCenterSelectBoxMultiselectRef.current = node) : null
              }
              onStatusChange={async (e: LookupItemModel) => {
                const val = getLookUpItemValue(e);
                await updateStateDynamically(setData, data, null, null, [
                  { key: "CostCenter_ID", value: val },
                ]);
              }}
              errorText={formik.errors.CostCenter_ID}
            />
          </div>
        )}
        {userConfiguration.userTransactionSetting.EnableDiscount && (
            <div className="col-xl-4 col-lg-4">
            <TextBox
              labelName={getLabelName("Discount")}
              isReadOnly={
                !userConfiguration.userTransactionSetting.EnableDiscount
              }
              type="price"
              preventNegative={true}
              inputName={"DiscountInCurrency"}
              inputValue={data.DiscountInCurrency}
              onChange={async (e: any) => {
                const valueInCurrency =
                  e.target.value != null ? Number(e.target.value) : 0;
                const value =
                  valueInCurrency === 0
                    ? 0
                    : getOriginalValueInCurrency(
                        valueInCurrency,
                        data.ValueCurrency
                      );
                  await updateStateDynamically(setData, data, null, null, [
                    { key: "DiscountInCurrency", value: valueInCurrency },
                    {
                      key: "Discount",
                      value: value,
                    },
                  ]);
                }}
                onBlur={async () => {
                  await handleChangeTransactionDiscount(setData, data);
                }}
              />
            </div>
        )}
          {userConfiguration.userTransactionSetting.EnableDiscount &&
              <div className="col-xl-4 col-lg-4">
              <TextBox
                labelName={getLabelName("Discount %")}
                isReadOnly={!userConfiguration.userTransactionSetting.EnableDiscount}
                type="price"
                preventNegative={true}
                inputName={"discountRatio"}
                inputValue={data.discountRatio}
                onChange={async (e: any) => {
                  const value = e.target.value != null ? Number(e.target.value) : 0;
                  if (value <= discountPercentageMaxValue) {
                    await updateStateDynamically(setData, data, null, null, [
                      { key: "discountRatio", value: value },
                    ]);
                  }
                }}
                onBlur={async () => {
                  await handleChangeTransactionDiscountRatio(setData, data);
                }}
              />
            </div>
          }
        <div className="col-xl-4 col-lg-4">

            <InputDatePicker
              timeMode={InputDatePickerTimeDisplayModeEnum.None}
              selectedDate={new Date(data.Date.toString())}
              className="form-control"
              InputLabel={"Date"}
              onChange={(date: Date) => {
                setData({
                  ...data,
                  Date: date,
                });
              }}
              maxDate={new Date()}
            />
          </div>
          {data.PaymentType_ID == PaymentTypeEnum.Delay &&
            <div className="col">
              <InputDatePicker
                selectedDate={data.DueDate ? new Date(data.DueDate.toString()) : undefined}
                timeMode={InputDatePickerTimeDisplayModeEnum.None}
                timeIntervals={15}
                className="form-control"
                InputLabel={"DueDate"}
                // selectsStart
                onChange={(date: Date) => {
                  setData({
                    ...data,
                    DueDate: date,
                  });
                }}
                minDate={data.minDueDate ? new Date(data.minDueDate.toString()) : undefined}
              //maxDate={new Date()}
              />
            </div>
          }
          {canSavePurchaseTransactionFromSales && (
        <div className="col-xl-4 col-lg-4">
              <SelectBox
                id="PaymentTypeForSupplierSelectBox"
                labelName={getLabelName("Supplier Payment Type")}
                isSingleSelect={true}
                selectedValues={
                  data.PaymentTypeIdForSupplier != null
                    ? [data.PaymentTypeIdForSupplier.toString()]
                    : []
                }
                source={paymentTypeList}
                //multiselectRef={paymentTypeSelectBoxMultiselectRef}
                //@ts-ignore
                multiselectRef={(node: MutableRefObject<any>) =>
                  node ? (paymentTypeForSupplierPaymentSelectBoxMultiselectRef.current = node) : null
                }
                onStatusChange={async (e: LookupItemModel) => {
                  const val = getLookUpItemValue(e);
                  await updateStateDynamically(setData, data, null, null, [
                    { key: "PaymentTypeIdForSupplier", value: val },
                  ]);
                }}
                errorText={formik.errors.PaymentTypeIdForSupplier}
              />
            </div>
        )}
          <TextBox
            inputName="totalMoneyInCurrency"
            labelName={getLabelName("Total")}
            inputValue={data.TotalMonyInCurrency}
            formatType={"decimal"}
            isReadOnly={true} isVisible={false}
          />
          {userConfiguration.companySetting.ApplyTax && (
            <TextBox
              labelName={getLabelName("Tax_Value")}
              inputValue={data.TotalTaxInCurrency}
              inputName="taxValueInCurrency"
              isReadOnly={true} isVisible={false}
            />
          )}
          <TextBox
            inputName="netBillInCurrency"
            labelName={getLabelName("Net Bill")}
            inputValue={data.TotalNetInCurrency}
            isReadOnly={true} isVisible={false}
          />
        </div>
      <div className="row mt-4 row-cols-1 row-cols-xxl-12 row-cols-xl-12 row-cols-lg-12 row-cols-md-12 row-cols-sm-12 g-md-12 g-sm-12">
          <TextBox
            labelName={getLabelName("Notes")}
            type="textarea"
            inputName="Note"
            inputValue={data.Note}
            onChange={async (e: any) => {
              await updateStateDynamically(
                setData,
                data,
                "Note",
                e.target.value
              );
            }}
            version={TextBoxVersionEnum.V2}
          // optimizeTyping={true}
          // resetKey={generateGuid()} // Add Code which changes on reset
        />
      </div>
      <UploadDialog
        requestId={data.ID}
        entityDocumentType="Trasnaction"
        setUploadedFiles={setUploadedFiles}
        uploadedFiles={uploadedFiles}
      />
    </div>
  );
  //#endregion
};
export const RegisterTransactionHeader = React.memo(
  RegisterTransactionHeaderComponent
);
